import type { OfficeCaptureEventInput, PostTripDefect } from '@chukua/common';

/** The driver steps the office can record. A pre-trip check is not here: the driver app has no such step. */
export const CAPTURE_STEPS = [
  'accepted',
  'departed',
  'stop_reached',
  'arrived',
  'loaded',
  'unloaded',
  'fuel_stop',
  'exception',
  'pod_captured',
  'post_trip_recorded',
] as const;
export type CaptureStep = (typeof CAPTURE_STEPS)[number];

interface StepDescription {
  /** In the picker. */
  label: string;
  /** "<noun> recorded for <driver>", in the toast. */
  noun: string;
}

export const CAPTURE_STEP_DESCRIPTIONS: Record<CaptureStep, StepDescription> = {
  accepted: { label: 'Accepted the trip', noun: 'Acceptance' },
  departed: { label: 'Departed', noun: 'Departure' },
  stop_reached: { label: 'Stop reached', noun: 'Stop' },
  arrived: { label: 'Arrived', noun: 'Arrival' },
  loaded: { label: 'Loaded', noun: 'Loading' },
  unloaded: { label: 'Unloaded', noun: 'Unloading' },
  fuel_stop: { label: 'Fuel stop', noun: 'Fuel stop' },
  // The only incident fact the trip log has is `exception`; it is a note, not an incident record.
  exception: { label: 'Incident', noun: 'Incident' },
  pod_captured: { label: 'Proof of delivery', noun: 'Proof of delivery' },
  post_trip_recorded: { label: 'Post-trip check', noun: 'Post-trip check' },
};

/** What an event type reads as on the list, including the ones only the phone sends. */
export function captureEventLabel(eventType: string): string {
  return (CAPTURE_STEP_DESCRIPTIONS as Record<string, StepDescription | undefined>)[eventType]?.label ?? eventType.replace(/_/g, ' ');
}

export function stepAsksForFuel(step: CaptureStep): boolean {
  return step === 'fuel_stop';
}

export function stepAsksForPhotos(step: CaptureStep): boolean {
  return step === 'pod_captured' || step === 'exception' || step === 'fuel_stop' || step === 'post_trip_recorded';
}

/** The post-trip check asks for the readings and defects; the server refuses it without the odometer and the fuel level. */
export function stepAsksForPostTrip(step: CaptureStep): boolean {
  return step === 'post_trip_recorded';
}

/** A defect as typed in the form; the phone's own uuid is minted when the event is built. */
export type CaptureDefectValues = Pick<PostTripDefect, 'area' | 'severity' | 'description'>;

/** An incident without words is no use to whoever reads the log, so the note is required for it. */
export function stepRequiresNote(step: CaptureStep): boolean {
  return step === 'exception';
}

export interface CaptureFormValues {
  step: CaptureStep;
  /** `datetime-local` text in the browser's zone: when it happened, which on a phone call is often earlier than now. */
  when: string;
  note?: string;
  litres?: number | null;
  station?: string;
  odometerKm?: number | null;
  fuelLevelPercent?: number | null;
  tyreCondition?: 'good' | 'worn' | 'damaged';
  bodyDamage?: 'none' | 'minor' | 'major';
  defects?: Partial<CaptureDefectValues>[];
}

function postTripPayload(values: CaptureFormValues): Record<string, unknown> {
  const payload: Record<string, unknown> = {};
  if (values.odometerKm != null) payload['odometerKm'] = values.odometerKm;
  if (values.fuelLevelPercent != null) payload['fuelLevelPercent'] = values.fuelLevelPercent;
  if (values.tyreCondition) payload['tyreCondition'] = values.tyreCondition;
  if (values.bodyDamage) payload['bodyDamage'] = values.bodyDamage;
  const defects = (values.defects ?? []).map((defect) => ({ id: crypto.randomUUID(), ...defect, description: defect.description?.trim() }));
  if (defects.length > 0) payload['defects'] = defects;
  return payload;
}

/** The body the driver app would have sent for the same step; `key` is the step's idempotency key, `mediaIds` the photos' own ids. */
export function buildCaptureEvent(values: CaptureFormValues, attempt: { key: string; mediaIds: string[] }): OfficeCaptureEventInput {
  const payload: Record<string, unknown> = {};
  const note = values.note?.trim();
  if (note) payload['note'] = note;
  if (stepAsksForFuel(values.step)) {
    if (values.litres != null) payload['litres'] = values.litres;
    if (values.station?.trim()) payload['station'] = values.station.trim();
  }
  if (stepAsksForPostTrip(values.step)) Object.assign(payload, postTripPayload(values));
  if (attempt.mediaIds.length > 0) payload['mediaIds'] = attempt.mediaIds;
  return { key: attempt.key, type: values.step, deviceTime: new Date(values.when).toISOString(), payload };
}

/** The value a `datetime-local` input takes for an instant, in the browser's zone. */
export function toLocalInputValue(instant: Date): string {
  const local = new Date(instant.getTime() - instant.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 16);
}
